import info from '@shared/api/graphQL/epgByDays/info';
import parser from '@shared/api/graphQL/parser';
import query from '@shared/api/graphQL/epgByDays/query';
import Direction from '@shared/types/enums/direction';
import Main from '@shared/types/enums/main';
import { ApolloError, useApolloClient, useQuery } from '@apollo/client';
import { EventMap, IEPGByDaysRequest } from '@shared/types/interfaces/event';
import { PaginationParams } from '@shared/types/interfaces/query';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Constants
 */
const EPG_PAGE_SIZE = 10;

/**
 * Defines the properties useEpg hook can receive.
 *
 * @property {string | undefined} [channelId] - The ID of the channel for which EPG data is fetched.
 * @property {number | undefined} [pageSize] - The number of elements per channel to fetch.
 */
interface Props {
  channelId: string;
  pageSize?: number;
}

/**
 * Epg by days hook. Fetches and returns next 7 days epg data.
 */
export default function useEpgByDays({ channelId, pageSize }: Props) {
  const [complete, setComplete] = useState(false);
  const [epg, setEpg] = useState<EventMap>({});
  const [variables, setVariables] = useState<{} | undefined>(undefined);

  const client = useApolloClient();
  const channel = useRef({
    id: channelId,
    pagination: {
      params: {
        page: 1,
        pageSize: pageSize || EPG_PAGE_SIZE,
      } as PaginationParams,
      maxItems: 0,
      maxPages: 1,
    },
  });

  // --------------------------
  // Event handlers

  /**
   * Request completion callback.
   */
  const onCompleted = useCallback(() => {
    // Complete request
    setComplete(true);
  }, []);

  /**
   * Request error callback.
   */
  const onError = useCallback((error: ApolloError) => {
    // Print error
    console.error(error.message);
    // Complete request
    setComplete(true);
  }, []);

  /**
   * Handles horizontal pagination for the specified content type.
   * Updates query variables to trigger data fetching in the desired direction.
   *
   * @param {Main} type - The specific content type to paginate.
   * @param {Direction} direction - The pagination direction.
   */
  const onPagination = useCallback((type: Main, direction: Direction) => {
    /**
     * Horizontal reel displacement
     */
    if (type === Main.RAILS && direction === Direction.HORIZONTAL) {
      const currentPage = channel.current.pagination.params.page;
      const nextPage = currentPage + 1;
      if (nextPage <= channel.current.pagination.maxPages) {
        // Set query variables so that new data is fetched
        channel.current.pagination.params.page = nextPage;
      }
      // Halt callback
      else {
        return;
      }
    } else {
      return;
    }

    /**
     * Fetches the next set of results for a paginated list field
     */
    fetchContents();
  }, []);

  // --------------------------
  // Logic related functions

  /**
   * Retrieves query required variables.
   *
   * @param {string} channelId - The channel to fetch data from.
   */
  const getVariables = useCallback(() => {
    return channel.current.id
      ? {
          epgHorizontalParams: channel.current.pagination.params,
          ...info(channel.current.id),
        }
      : undefined;
  }, []);

  /**
   * Requests data based on the current channel's identifier
   *
   * @param {string} channelId - The channel to fetch data from.
   */
  const fetchContents = useCallback(() => {
    const variables = getVariables();
    const params = {
      query,
      variables,
    };
    const data: IEPGByDaysRequest | null = client.readQuery(params);
    const fetchedPage =
      data?.epgsByDays?.items?.reduce((prev, item) => {
        const page = Math.max(
          Math.ceil((item.epgs?.length || 0) / (pageSize || EPG_PAGE_SIZE)),
          1
        );
        if (page > prev) {
          return page;
        }
        return prev;
      }, 0) || 0;

    // Fetch new page if possible
    if (fetchedPage < channel.current.pagination.maxPages) {
      // Update page to fetch if current is fetched already
      if (fetchedPage >= channel.current.pagination.params.page) {
        // Set current page to next page if already fetched
        if (fetchedPage > channel.current.pagination.params.page) {
          channel.current.pagination.params.page = fetchedPage + 1;
        } else {
          return;
        }
        // Set new params
        if (params.variables?.epgHorizontalParams) {
          params.variables.epgHorizontalParams.page =
            channel.current.pagination.params.page;
        }
      }
      fetchMore(params);
    }
    // If data was already fetched, set current page
    else {
      channel.current.pagination.params.page = fetchedPage;
    }
  }, [channelId]);

  /**
   * Parses a given response and updates main contents
   *
   * @param {IEPGByDaysRequest} response - Retrieved EPG data to be parsed.
   */
  const updateContents = useCallback((response: IEPGByDaysRequest) => {
    try {
      // Update epg and pagination data
      const result = parser.epgsByDays(response);
      if (result) {
        channel.current.pagination.maxItems = result.maxItems;
        channel.current.pagination.maxPages = result.maxPages;
        setEpg(result.map);
      }
    } catch (e) {
      // Print error
      console.error(e);
    }
  }, []);

  // --------------------------
  // Query hook

  /**
   * Fetch epg data via graphql using the query hook
   */
  const { data, fetchMore } = useQuery(query, {
    skip: !variables,
    variables,
    onCompleted,
    onError,
  });

  // --------------------------
  // Effect hooks

  /**
   * Fetch channel's associated data
   */
  useEffect(() => {
    if (channelId) {
      if (channel.current.id !== channelId) {
        channel.current = {
          id: channelId,
          pagination: {
            params: {
              page: 0,
              pageSize: pageSize || EPG_PAGE_SIZE,
            },
            maxItems: 0,
            maxPages: 1,
          },
        };
      }
      setVariables(getVariables());
    }
  }, [channelId]);

  /**
   * Updates data on change
   */
  useEffect(() => {
    data && updateContents(data);
  }, [data]);

  // --------------------------

  /**
   *  @param {boolean} complete - Whether the request is undergoing or not.
   *  @param {EventMap} epg - The processed EPG data.
   *  @param {() => void} onPagination - Callback function to handle pagination.
   */
  return {
    complete,
    epg,
    onPagination,
  };
}
